import {Icon,type IconName} from './Icon';
import { NavLink, type To } from 'react-router';

export const SUMMARY_TONES = [
  'neutral',
  'information',
  'success',
  'warning',
  'danger',
] as const;

export type SummaryTone = (typeof SUMMARY_TONES)[number];

export type SummaryNavigationItem = Readonly<{
  label: string;
  to: To;
  value?: string | number;
  tone?: SummaryTone | undefined;
  end?: boolean | undefined;
  state?: never;
  unavailableLabel?: never;
}>;

type AvailableSummaryMetric = Readonly<{
  state?: 'available';
  value: string | number;
  tone?: SummaryTone | undefined;
  unavailableLabel?: never;
}>;

type UnavailableSummaryMetric = Readonly<{
  state: 'unavailable';
  value?: never;
  tone?: never;
  unavailableLabel?: string;
}>;

export type SummaryMetricItem = Readonly<{
  label: string;
  to?: never;
  end?: never;
}> & (AvailableSummaryMetric | UnavailableSummaryMetric);

export type SummaryStripProps =
  | Readonly<{
      mode: 'navigation';
      ariaLabel: string;
      items: ReadonlyArray<SummaryNavigationItem>;
    }>
  | Readonly<{
      mode: 'metric';
      ariaLabel: string;
      items: ReadonlyArray<SummaryMetricItem>;
    }>;

const toneLabels: Record<Exclude<SummaryTone, 'neutral'>, string> = {
  information: 'Information',
  success: 'Success',
  warning: 'Needs attention',
  danger: 'Urgent attention',
};

const toneSymbols: Record<Exclude<SummaryTone, 'neutral'>, IconName> = {
  information: 'info',
  success: 'circle-check',
  warning: 'triangle-alert',
  danger: 'circle-alert',
};

function SummaryItemLabel({ label, tone = 'neutral' }: Readonly<{ label: string; tone?: SummaryTone | undefined }>) {
  return <span className="summary-strip__label">
    {tone !== 'neutral' && <>
      <span className="sr-only">{toneLabels[tone]}: </span>
      <span className="summary-strip__tone" aria-hidden="true" title={toneLabels[tone]}><Icon name={toneSymbols[tone]}/></span>
    </>}
    <span>{label}</span>
  </span>;
}

function itemClassName(tone: SummaryTone | undefined, unavailable = false) {
  return `summary-strip__item summary-strip__item--${unavailable ? 'unavailable' : tone ?? 'neutral'}`;
}

function NavigationSummaryStrip({ ariaLabel, items }: Extract<SummaryStripProps, { mode: 'navigation' }>) {
  return <nav className="summary-strip summary-strip--navigation" aria-label={ariaLabel}>
    <ul className="summary-strip__items">
      {items.map((item) => <li className={itemClassName(item.tone)} key={`${item.label}:${String(item.to)}`}>
        <NavLink
          className={({ isActive }) => `summary-strip__link${isActive ? ' summary-strip__link--active' : ''}`}
          end={item.end ?? true}
          to={item.to}
        >
          {({ isActive }) => <>
            <SummaryItemLabel label={item.label} tone={item.tone} />
            {isActive && <span className="summary-strip__active">Current</span>}
            {item.value !== undefined && <strong className="summary-strip__value">{item.value}</strong>}
          </>}
        </NavLink>
      </li>)}
    </ul>
  </nav>;
}

function MetricSummaryStrip({ ariaLabel, items }: Extract<SummaryStripProps, { mode: 'metric' }>) {
  return <section className="summary-strip summary-strip--metric" aria-label={ariaLabel}>
    <dl className="summary-strip__items">
      {items.map((item) => {
        const unavailable = item.state === 'unavailable';
        return <div className={itemClassName(item.tone, unavailable)} key={item.label}>
          <dt><SummaryItemLabel label={item.label} tone={item.tone} /></dt>
          <dd
            className={`summary-strip__value${unavailable ? ' summary-strip__value--unavailable' : ''}`}
            data-state={unavailable ? 'unavailable' : 'available'}
          >{unavailable ? item.unavailableLabel ?? 'Unavailable' : item.value}</dd>
        </div>;
      })}
    </dl>
  </section>;
}

export function SummaryStrip(props: SummaryStripProps) {
  return props.mode === 'navigation'
    ? <NavigationSummaryStrip {...props} />
    : <MetricSummaryStrip {...props} />;
}
